import Link from "next/link";
import Head from "next/head";
import { PageMeta } from "~/components/utils/PageMeta";

export default function HowToWriteProject() {
  const metaTitle = "How to write a case study that gets you hired | Showspace";
  const metaDescription =
    "At the heart of every portfolio are work samples and case studies. We call those 'Projects' here at Showspace. While our GPT-Powered AI can help you quickly generate a base template, we recommend using the template below to continue to refine the narrative.";
  const metaImage =
    "https://chmqmeyyaiwfybqgcdoy.supabase.co/storage/v1/object/public/application/resources/sixty-on-demand-project-example.png";

  const metaUrl =
    "https://showspace.so/resources/how-to-write-a-work-sample-or-case-study-for-your-portfolio";

  return (
    <>
      <PageMeta
        description={metaDescription}
        imageUrl={metaImage}
        title={metaTitle}
        url={metaUrl}
      />

      <div className="blog-post">
        <div className="blog-accent">
          <div
            className="relative mx-auto h-full max-w-prose text-lg"
            aria-hidden="true"
          >
            <svg
              className="absolute top-12 left-full translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384"
            >
              <defs>
                <pattern
                  id="74b3fd99-0a6f-4271-bef2-e80eeafdf357"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)"
              />
            </svg>
            <svg
              className="absolute top-1/2 right-full -translate-y-1/2 -translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384"
            >
              <defs>
                <pattern
                  id="f210dbf6-a58d-4871-961e-36d5016a0f49"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)"
              />
            </svg>
            <svg
              className="absolute bottom-12 left-full translate-x-32 transform"
              width={404}
              height={384}
              fill="none"
              viewBox="0 0 404 384"
            >
              <defs>
                <pattern
                  id="d3eb07ae-5182-43e6-857d-35c643af9034"
                  x={0}
                  y={0}
                  width={20}
                  height={20}
                  patternUnits="userSpaceOnUse"
                >
                  <rect
                    x={0}
                    y={0}
                    width={4}
                    height={4}
                    className="text-gray-200"
                    fill="currentColor"
                  />
                </pattern>
              </defs>
              <rect
                width={404}
                height={384}
                fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)"
              />
            </svg>
          </div>
        </div>
        <div className="blog-container">
          <div className="blog-header">
            <span className="category">Resources</span>
            <h1 className="">
              How to write a work sample or case study that gets you hired
            </h1>
            <div className="author-section">
              By{" "}
              <Link href="/s/david" target="_blank" className="author">
                David Head
              </Link>
            </div>
          </div>
          <div className="blog-body-container">
            <p>
              At the heart of every portfolio are work samples and case studies.
              We call those "Projects" here at Showspace.
            </p>
            <p>
              Projects can be written from the first person perspective or the
              team perspective. If multiple people contributed to the project,
              we recommend writing from the team perspective. Then when we
              release the "tagging" feature (ETA Q1 '23), you can tag them and
              they can have the project on their portfolio too! This is a great
              way to memorialize your work together and build team bonds even
              after your work is done.
            </p>
            <p>
              While our GPT-Powered AI can help you quickly generate a base
              template, we recommend using the template below to continue to
              refine the narrative.
            </p>
            <hr />
            <h2>Background</h2>
            <p>
              Optional section where you can provide background information on
              the situation, and explain why it is important or relevant.
              Including this is case-specific depending on the audience you're
              intending to distribute your project to.
            </p>
            <p>
              If you expect to distribute this project outside your organization
              at any point, we recommend sharing context accordingly.
            </p>
            <p>For example:</p>
            <ul>
              <li>Company context</li>
              <li>Industry context</li>
            </ul>
            <h2>Problem</h2>
            <p>
              Describe the problem or challenge the team faced that inspired the
              work. Aim for at least 1-2 short paragraphs.
            </p>
            <ul>
              <li>Why was it painful?</li>
              <li>Can you quantify the pain?</li>
              <li>How did you know users were frustrated?</li>
              <li>
                Do you have screenshots, pictures, quotes, videos, or any
                content to support the story with?
              </li>
            </ul>
            <h2>Solution</h2>
            <p>
              Explain the solution or approach that was taken to address the
              problem. Aim for at least 1-2 short paragraphs here as well. Feel
              free to add in your team members names to the project where
              relevant.
            </p>
            <ul>
              <li>What was the process of coming up with the solution?</li>
              <li>What was the solution you arrived at?</li>
              <li>What were the challenges along the way?</li>
              <li>
                Any screenshots, pictures, videos, or other content to add?
              </li>
            </ul>
            <h2>Results</h2>
            <p>
              Share the results or outcomes of the solution, including
              measurable success or impact. User quotes and metrics are also
              great here. We recommend using bullets.
            </p>
            <ul>
              <li>What was the result of the project?</li>
              <li>Can you quantify it?</li>
            </ul>
            <h2>Team/Contribution</h2>
            <p>
              What did you (and if you had a team) and everyone contribute to
              the project? We recommend bulleting out your team here as well, as
              shown below.
            </p>
            <p>
              If you create a project just for yourself, we recommend calling
              this “Contribution” where you bullet what you did in a similar
              style to your resume.
            </p>
            <strong>John Smith</strong> - Product Manager
            <ul>
              <li>Led team of 5 engineers and a designer</li>
              <li>Conducted user interviews to better inform solution</li>
            </ul>
            <strong>Jane Robinson</strong> - Senior Full Stack Engineer
            <ul>
              <li>Develped web app in Next.js and Supabase</li>
              <li>Conducted user interviews to better inform solution</li>
            </ul>
            <hr />
            <h2>Example Projects</h2>
            <p>
              While every project is different, and yours may not fit into the
              mold above, here are some examples of projects we think are good
              to look to for inspiration. You can also browse the{" "}
              <Link href="/explore" target="_blank">
                Explore
              </Link>{" "}
              page.
            </p>
            <p>
              <Link href="/p/8" target="_blank">
                On Demand Web App Help for Sixty
              </Link>
            </p>
            <figure className="pb-8">
              <img
                src="https://chmqmeyyaiwfybqgcdoy.supabase.co/storage/v1/object/public/application/resources/sixty-on-demand-project-example.png"
                alt="Example portfolio project on Showspace - On Demand Web App Help for Sixty"
                width={1310}
                height={873}
              />
            </figure>
            <p>
              <Link href="/p/240" target="_blank">
                Bootstrapped Furnished Housing Startup for StayNÔM
              </Link>
            </p>
            <figure className="pb-8">
              <img
                src="https://chmqmeyyaiwfybqgcdoy.supabase.co/storage/v1/object/public/application/resources/staynom-project-example.png"
                alt="Example portfolio project on Showspace - Bootstrapped Furnished Housing Startup for StayNÔM"
                width={1310}
                height={873}
              />
            </figure>
            <p>
              <Link href="/p/13" target="_blank">
                Student Master Viewer for BloomTech
              </Link>
            </p>
            <figure>
              <img
                src="https://chmqmeyyaiwfybqgcdoy.supabase.co/storage/v1/object/public/application/resources/smv-project-example.png"
                alt="Example portfolio project on Showspace - Student Master Viewer for BloomTech"
                width={1310}
                height={873}
              />
            </figure>
          </div>
        </div>
      </div>
    </>
  );
}
